import { Dispatch, FC, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { TLegacyDriver } from "../../../api/types/Driver";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import dashboardMenuIcons from "../../../assets/svg/dashboardMenuIcons";
import HeaderView from "../headerView";
import TripDetails from "../tripDetails";
import { DateGroup } from "./useMultiDateTripData";
import { tripDisplayNumberOr, tripDisplayNumbers } from "./tripNumbering";

type Props = {
  dateGroups: DateGroup[];
  tripKey: string;
  vehicleDeviceId?: string;
  vehicleSubtitle?: string;
  assignedDriver?: TLegacyDriver;
  selectedTripPoint: VehicleMapDataPoint | null;
  setSelectedTripPoint: Dispatch<SetStateAction<VehicleMapDataPoint | null>>;
  onBack: () => void;
  onReplayEvents: () => void;
  replayInProgress: boolean;
  replayPercentage: number | null;
};

// multi-day equivalent of the single-day Trip Details screen. opened from
// a row's View Details or by clicking the trip's route on the map.
export const TripDetailPanel: FC<Props> = ({
  dateGroups,
  tripKey,
  vehicleDeviceId,
  vehicleSubtitle,
  assignedDriver,
  selectedTripPoint,
  setSelectedTripPoint,
  onBack,
  onReplayEvents,
  replayInProgress,
  replayPercentage,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "vehicleMap" });

  const [dateKey, tripIndexKey] = tripKey.split("__");
  const group = dateGroups.find((item) => item.dateKey === dateKey);
  const tripIndex = Number(tripIndexKey);
  const tripPoints = group?.trips[tripIndex];

  if (!group || !tripPoints) {
    return (
      <>
        <HeaderView
          title={t("tripDetails.heading")}
          showBackButton
          backButtonCallback={onBack}
          wrapperClassName="flex justify-between px-4 py-2 mt-4"
        />
        <div className="px-4 py-6 text-xs text-heading-gray888">
          This trip is no longer available.
        </div>
      </>
    );
  }

  return (
    <div className="flex-grow min-h-0 flex flex-col overflow-y-auto overscroll-contain">
      <HeaderView
        title={t("tripDetails.heading")}
        showBackButton
        backButtonCallback={onBack}
        wrapperClassName="flex justify-between px-4 py-2 mt-4 shrink-0"
      />

      <div className="mt-2 mx-3 flex items-start text-accent-blue-dark shrink-0">
        <img
          className="bg-accent-blue-pale rounded-lg px-2 size-10"
          src={dashboardMenuIcons.admins.vehicle}
          alt=""
        />
        <div className="flex flex-col gap-2 ms-4">
          <h3 className="font-bold text-base leading-4">{vehicleDeviceId}</h3>
          <h4 className="text-xs">{vehicleSubtitle}</h4>
        </div>
      </div>

      {/* which day this trip belongs to - the date-grouped list isn't
          visible from here, so the day colour/label has to be restated.
          (comparing routes is offered on the map itself, alongside the
          other map controls, rather than duplicated here.) */}
      <div className="flex items-center gap-2 px-4 mt-3 shrink-0">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <span
            className="inline-block size-3 rounded-full shrink-0"
            style={{ backgroundColor: group.color }}
          />
          {group.label}
        </span>
      </div>

      <div className="mt-2">
        <TripDetails
          assignedDriver={assignedDriver}
          tripNumber={tripDisplayNumberOr(
            tripDisplayNumbers(group.trips),
            tripIndex
          )}
          tripPoints={tripPoints}
          selectedTripPoint={selectedTripPoint}
          setSelectedTripPoint={setSelectedTripPoint}
          colorOverride={group.color}
          detailedView
          onClick={() => {}}
          onReplayEvents={onReplayEvents}
          replayInProgress={replayInProgress}
          replayPercentage={replayPercentage}
        />
      </div>
    </div>
  );
};
